import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { UserPlus, Bell, Users, LogOut, Shield, GraduationCap, MessageCircle, Image as ImageIcon, Settings, Mail, MessageSquare } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { schoolInfo } from '../utils/mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboardPage = () => {
  const navigate = useNavigate();
  const [adminUsername, setAdminUsername] = useState('');
  const [loading, setLoading] = useState(true);
  const [showRemindersModal, setShowRemindersModal] = useState(false);
  const [pendingFeesStudents, setPendingFeesStudents] = useState([]);
  const [pendingHomework, setPendingHomework] = useState([]);
  const [loadingReminders, setLoadingReminders] = useState(false);
  
  // Statistics state
  const [stats, setStats] = useState({
    students: 0,
    notices: 0,
    loginsToday: 0,
    unreadMessages: 0
  });
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    // Check if admin is authenticated
    const verifyAdmin = async () => {
      const token = localStorage.getItem('adminToken');
      const username = localStorage.getItem('adminUsername');

      if (!token) {
        navigate('/admin/login');
        return;
      }

      try {
        await axios.get(`${BACKEND_URL}/api/admin/verify`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAdminUsername(username || 'Admin');
        setLoading(false);
        
        // Fetch dashboard statistics
        fetchDashboardStats();
      } catch (err) {
        console.error('Verification failed:', err);
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUsername');
        navigate('/admin/login');
      }
    };

    verifyAdmin();

  const fetchDashboardStats = async () => {
    setStatsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/dashboard/stats`);
      const data = response.data;
      
      setStats({
        students: data.students.total,
        notices: data.notices.total,
        loginsToday: data.logins.today,
        unreadMessages: data.messages.unread
      });
    } catch (err) {
      console.error('Failed to fetch dashboard stats:', err);
      toast.error('Failed to load dashboard statistics');
    } finally {
      setStatsLoading(false);
    }
  };

  }, [navigate]);

  const handleLogout = async () => {
    const token = localStorage.getItem('adminToken');
    
    try {
      await axios.post(`${BACKEND_URL}/api/admin/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      console.error('Logout error:', err);
    }

    // Clear localStorage
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUsername');
    localStorage.removeItem('isAdminAuthenticated');
    
    // Clear sessionStorage
    sessionStorage.clear();
    
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  const fetchReminderData = async () => {
    setLoadingReminders(true);
    try {
      const [feesResponse, homeworkResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/reminders/pending-fees`),
        axios.get(`${BACKEND_URL}/api/reminders/all-pending-homework`)
      ]);
      setPendingFeesStudents(feesResponse.data);
      setPendingHomework(homeworkResponse.data);
    } catch (error) {
      console.error('Error fetching reminder data:', error);
      toast.error('Failed to load reminder data');
    } finally {
      setLoadingReminders(false);
    }
  };

  const handleOpenReminders = () => {
    setShowRemindersModal(true);
    fetchReminderData();
  };

  const sendFeeReminder = (student) => {
    const message = `🔔 AMTEE PUBLIC SCHOOL

Dear ${student.parent_name},

This is a friendly reminder regarding pending school fees for ${student.student_name} (${student.class_name}).

💰 Fee Details:
Total Fees: ₹${student.total_fees}
Paid: ₹${student.paid_amount}
Due Amount: ₹${student.due_amount}

Please clear the dues at your earliest convenience.

Thank you for your cooperation.

- AMTEE PUBLIC SCHOOL`;

    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${student.mobile_number}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
    toast.success(`Opening WhatsApp for ${student.student_name}`);
  };

  const sendHomeworkReminder = async (homework) => {
    try {
      // Get students who haven't submitted
      const response = await axios.get(`${BACKEND_URL}/api/reminders/pending-homework/${homework.homework_id}`);
      const pendingStudents = response.data;

      if (pendingStudents.length === 0) {
        toast.info('All students have submitted this homework!');
        return;
      }

      // Create a message for each parent
      pendingStudents.forEach((student) => {
        const message = `🔔 AMTEE PUBLIC SCHOOL

Dear ${student.parent_name},

This is a reminder for pending homework submission for ${student.student_name}.

📚 Homework Details:
Title: ${homework.title}
Description: ${homework.description}
Due Date: ${homework.date}
Class: ${homework.class_name}

Please ensure your child submits the homework soon.

Thank you!

- ${homework.teacher_name}`;

        const encodedMessage = encodeURIComponent(message);
        const whatsappUrl = `https://wa.me/${student.mobile_number}?text=${encodedMessage}`;
        
        // Open in new tab with a small delay to avoid popup blocking
        setTimeout(() => {
          window.open(whatsappUrl, '_blank');
        }, 100);
      });

      toast.success(`Opening WhatsApp for ${pendingStudents.length} parent(s)`);
    } catch (error) {
      console.error('Error sending homework reminder:', error);
      toast.error('Failed to send homework reminder');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src={schoolInfo.logo} 
                alt="AMTEE PUBLIC SCHOOL" 
                className="h-12 w-12 object-contain"
              />
              <div>
                <h1 className="text-xl font-bold text-gray-900">Admin Dashboard</h1>
                <p className="text-sm text-gray-600">{schoolInfo.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/admin/settings')}
                className="flex items-center gap-2 text-gray-600 hover:text-blue-600"
              >
                <Settings className="h-4 w-4" />
                <span className="hidden sm:inline">Settings</span>
              </Button>
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{adminUsername}</p>
                <p className="text-xs text-gray-500">Administrator</p>
              </div>
              <Button 
                variant="outline" 
                onClick={handleLogout}
                className="flex items-center gap-2"
              >
                <LogOut className="h-4 w-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Welcome Card */}
        <Card className="mb-8 bg-gradient-to-r from-[#4A90E2] to-[#357ABD] text-white border-0">
          <CardContent className="py-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold mb-2">Welcome back, {adminUsername}!</h2>
                <p className="text-gray-100">Manage your school system from here</p>
              </div>
              <Shield className="h-16 w-16 opacity-30" />
            </div>
          </CardContent>
        </Card>

        {/* Admin Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-7">
          {/* Manage Students Card */}
          <Card 
            className="shadow-xl hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-purple-600 rounded-2xl"
          >
            <CardHeader>
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                <Users className="h-8 w-8 text-purple-600" />
              </div>
              <CardTitle className="text-xl">Manage Students</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription className="text-base">
                Add, view, edit, and manage all registered students
              </CardDescription>
              <Button 
                onClick={() => navigate('/admin/students')}
                className="w-full mt-4 bg-purple-600 hover:bg-purple-700 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
              >
                <Users className="h-5 w-5" />
                Manage Students
              </Button>
            </CardContent>
          </Card>

          <Card 
            className="shadow-xl hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-blue-600 rounded-2xl"
          >
            <CardHeader>
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <GraduationCap className="h-8 w-8 text-blue-600" />
              </div>
              <CardTitle className="text-xl">Manage Teachers</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription className="text-base">
                Add, edit, and manage teacher accounts and assignments
              </CardDescription>
              <Button 
                onClick={() => navigate('/admin/manage-teachers')}
                className="w-full mt-4 bg-blue-600 hover:bg-blue-700 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Manage Teachers
              </Button>
            </CardContent>
          </Card>

          <Card 
            className="shadow-xl hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-green-600 rounded-2xl"
          >
            <CardHeader>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <Bell className="h-8 w-8 text-green-600" />
              </div>
              <CardTitle className="text-xl">Manage Notices</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription className="text-base">
                Create, edit, and delete school notices and announcements
              </CardDescription>
              <Button 
                onClick={() => navigate('/admin/notices')}
                className="w-full mt-4 bg-green-600 hover:bg-green-700 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Manage Notices
              </Button>
            </CardContent>
          </Card>

          <Card 
            className="shadow-xl hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-orange-600 rounded-2xl"
          >
            <CardHeader>
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                <MessageCircle className="h-8 w-8 text-orange-600" />
              </div>
              <CardTitle className="text-xl">Send Reminders</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription className="text-base">
                Send WhatsApp reminders for pending fees and homework
              </CardDescription>
              <Button 
                onClick={handleOpenReminders}
                className="w-full mt-4 bg-orange-600 hover:bg-orange-700"
              >
                Open Reminders
              </Button>
            </CardContent>
          </Card>

          <Card 
            className="shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-indigo-600"
          >
            <CardHeader>
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
                <Mail className="h-8 w-8 text-indigo-600" />
              </div>
              <CardTitle className="text-xl">Contact Messages</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription className="text-base">
                View and manage messages from contact form submissions
              </CardDescription>
              <Button 
                onClick={() => navigate('/admin/messages')}
                className="w-full mt-4 bg-indigo-600 hover:bg-indigo-700"
              >
                View Messages
              </Button>
            </CardContent>
          </Card>

          <Card 
            className="shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-pink-600"
          >
            <CardHeader>
              <div className="w-16 h-16 bg-pink-100 rounded-full flex items-center justify-center mb-4">
                <ImageIcon className="h-8 w-8 text-pink-600" />
              </div>
              <CardTitle className="text-xl">Homepage Images</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription className="text-base">
                Manage carousel images displayed on homepage
              </CardDescription>
              <Button 
                onClick={() => navigate('/admin/homepage-images')}
                className="w-full mt-4 bg-pink-600 hover:bg-pink-700"
              >
                Manage Images
              </Button>
            </CardContent>
          </Card>

          <Card 
            className="shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-indigo-600"
          >
            <CardHeader>
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mb-4">
                <GraduationCap className="h-8 w-8 text-indigo-600" />
              </div>
              <CardTitle className="text-xl">Admission Settings</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription className="text-base">
                Control admission status, session year, and application dates
              </CardDescription>
              <Button 
                onClick={() => navigate('/admin/admission-settings')}
                className="w-full mt-4 bg-indigo-600 hover:bg-indigo-700"
              >
                Manage Admissions
              </Button>
            </CardContent>
          </Card>

          <Card 
            className="shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-teal-600"
          >
            <CardHeader>
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-4">
                <MessageSquare className="h-8 w-8 text-teal-600" />
              </div>
              <CardTitle className="text-xl">Testimonials</CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription className="text-base">
                Manage "What Parents Say" section on homepage
              </CardDescription>
              <Button 
                onClick={() => navigate('/admin/testimonials')}
                className="w-full mt-4 bg-teal-600 hover:bg-teal-700"
              >
                Manage Testimonials
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium text-gray-600">Total Students</CardTitle>
            </CardHeader>
            <CardContent>
              {statsLoading ? (
                <div className="animate-pulse">
                  <div className="h-10 bg-gray-200 rounded w-20"></div>
                </div>
              ) : (
                <>
                  <p className="text-3xl font-bold text-gray-900">{stats.students}</p>
                  <p className="text-xs text-gray-500 mt-1">Registered in system</p>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium text-gray-600">Active Notices</CardTitle>
            </CardHeader>
            <CardContent>
              {statsLoading ? (
                <div className="animate-pulse">
                  <div className="h-10 bg-gray-200 rounded w-20"></div>
                </div>
              ) : (
                <>
                  <p className="text-3xl font-bold text-gray-900">{stats.notices}</p>
                  <p className="text-xs text-gray-500 mt-1">Published notices</p>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-medium text-gray-600">Parent Logins Today</CardTitle>
            </CardHeader>
            <CardContent>
              {statsLoading ? (
                <div className="animate-pulse">
                  <div className="h-10 bg-gray-200 rounded w-20"></div>
                </div>
              ) : (
                <>
                  <p className="text-3xl font-bold text-gray-900">{stats.loginsToday}</p>
                  <p className="text-xs text-gray-500 mt-1">Active sessions</p>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Reminders Modal */}
      <Dialog open={showRemindersModal} onOpenChange={setShowRemindersModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="h-6 w-6 text-orange-600" />
              Send WhatsApp Reminders
            </DialogTitle>
            <DialogDescription>
              Send reminders to parents for pending fees and homework submissions
            </DialogDescription>
          </DialogHeader>

          {loadingReminders ? (
            <div className="py-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-orange-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading reminder data...</p>
            </div>
          ) : (
            <Tabs defaultValue="fees" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="fees" className="flex items-center gap-2">
                  💰 Pending Fees ({pendingFeesStudents.length})
                </TabsTrigger>
                <TabsTrigger value="homework" className="flex items-center gap-2">
                  📚 Pending Homework ({pendingHomework.length})
                </TabsTrigger>
              </TabsList>

              {/* Pending Fees Tab */}
              <TabsContent value="fees">
                {pendingFeesStudents.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <div className="text-5xl mb-4">🎉</div>
                    <p className="text-lg font-semibold">All fees are up to date!</p>
                    <p className="text-sm">No pending fee reminders to send</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pendingFeesStudents.map((student) => (
                      <Card key={student.id} className="border-l-4 border-l-red-500">
                        <CardContent className="pt-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="font-bold text-gray-900 mb-1">{student.student_name}</p>
                              <p className="text-sm text-gray-600 mb-2">
                                {student.class_name} • Parent: {student.parent_name}
                              </p>
                              <div className="bg-red-50 p-3 rounded-lg inline-block">
                                <p className="text-xs text-gray-500">Due Amount</p>
                                <p className="text-2xl font-bold text-red-600">₹{student.due_amount}</p>
                                <p className="text-xs text-gray-500 mt-1">
                                  Total: ₹{student.total_fees} | Paid: ₹{student.paid_amount}
                                </p>
                              </div>
                            </div>
                            <Button
                              onClick={() => sendFeeReminder(student)}
                              className="bg-green-600 hover:bg-green-700 text-white"
                            >
                              <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                              </svg>
                              Send Reminder
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Pending Homework Tab */}
              <TabsContent value="homework">
                {pendingHomework.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <div className="text-5xl mb-4">✅</div>
                    <p className="text-lg font-semibold">All homework submitted!</p>
                    <p className="text-sm">No pending homework reminders to send</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pendingHomework.map((hw) => (
                      <Card key={hw.homework_id} className="border-l-4 border-l-orange-500">
                        <CardContent className="pt-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="font-bold text-gray-900 mb-1">{hw.title}</p>
                              <p className="text-sm text-gray-600 mb-2">
                                {hw.class_name} • {hw.teacher_name}
                              </p>
                              <p className="text-sm text-gray-700 mb-3">{hw.description}</p>
                              <div className="flex items-center gap-4 text-sm">
                                <div className="bg-blue-50 px-3 py-1 rounded-full">
                                  <span className="text-gray-600">Due: </span>
                                  <span className="font-semibold text-blue-700">{hw.date}</span>
                                </div>
                                <div className="bg-orange-50 px-3 py-1 rounded-full">
                                  <span className="text-gray-600">Pending: </span>
                                  <span className="font-bold text-orange-700">{hw.pending}/{hw.total_students}</span>
                                </div>
                                <div className="bg-green-50 px-3 py-1 rounded-full">
                                  <span className="text-gray-600">Submitted: </span>
                                  <span className="font-semibold text-green-700">{hw.submitted}</span>
                                </div>
                              </div>
                            </div>
                            <Button
                              onClick={() => sendHomeworkReminder(hw)}
                              className="bg-green-600 hover:bg-green-700 text-white ml-4"
                            >
                              <svg className="h-4 w-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                              </svg>
                              Send Reminders
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboardPage;
